/**
 * Design Preview — nutzt die FL-App-Design-Sprache (Dashboard/Finanzen/Steuern).
 *
 * Route: /design-preview (kein Auth)
 *
 * Design-Regeln (aus Dashboard.js, Finances.js, App-Header):
 *   - Top-Navigation horizontal mit grünen Pill-Items
 *   - Cards: shadcn Card mit border-t-4 Farbakzent
 *   - Icon-Boxen: p-2.5 rounded-xl bg-emerald-50 text-emerald-600
 *   - Primary-Button: bg-emerald-600 hover:bg-emerald-700 (NICHT slate-900)
 *   - rounded-lg / rounded-xl überall
 *   - font-mono für alle Zahlen (JetBrains Mono)
 *   - Font: Manrope für Heading, DM Sans body
 *   - Page-Background: bg-slate-50 (oder weiß)
 */
import React, { useState } from "react";
import {
  Lock, MessageCircle, CheckSquare, FileQuestion, StickyNote, AlertCircle,
  Paperclip, Send, Share2, Search, Upload, Folder, ChevronRight, Bell,
  Users, FileText, Clock, ArrowRight, Filter, Eye, Download, Calculator,
  Inbox as InboxIcon, FolderOpen, TrendingUp, LayoutDashboard, Briefcase,
  Receipt, Wallet, LogOut, UserPlus, Menu,
} from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";

const H = { fontFamily: "Manrope, sans-serif" };

// ─────────────────────────── Fake Data ────────────────────────────
const fakeClients = [
  { id: "c1", name: "Lena Scholz", initials: "LS", waiting_for_me: 3, urgent: 1, last: "vor 14 Min" },
  { id: "c2", name: "Dr. Klaus Breuer", initials: "KB", waiting_for_me: 2, urgent: 0, last: "vor 2 Std" },
  { id: "c3", name: "Nebula GmbH", initials: "NG", waiting_for_me: 1, urgent: 0, last: "gestern" },
];

const fakeThreads = [
  { id: "t1", type: "request", subject: "Beleg Restaurant 12.04. fehlt", preview: "Kannst du bitte den Original-Beleg …", owner: "Du", time: "14 Min", priority: "urgent", unread: true },
  { id: "t2", type: "question", subject: "Umsatzsteuer für Beratung an AT-Kunde?", preview: "Rechnung #2026-017 ist raus, aber …", owner: "Lena", time: "2 Std", priority: "normal", unread: false },
  { id: "t3", type: "task", subject: "Kontoauszug März hochladen", preview: "Für Q1-Abschluss brauche ich noch …", owner: "Lena", time: "gestern", priority: "normal", unread: false },
  { id: "t4", type: "note", subject: "Steuerberechnung Q1 2026", preview: "Anbei die Berechnung. Zahlbetrag …", owner: "Du", time: "2 Tage", priority: "normal", unread: false },
];

const fakeMessages = [
  { id: "m1", author: "SB", name: "Sabine Steuerberater", time: "Heute 09:14", body: "Hallo Lena, für deine UStVA Q1 fehlt mir noch der Original-Beleg für den Restaurantbesuch am 12.04. (€87,50). Kannst du den bitte fotografieren und hochladen?" },
  { id: "m2", author: "FL", name: "Lena Scholz", time: "Heute 11:02", body: "Hi Sabine, den Beleg habe ich leider verloren. Ich habe aber die Kreditkarten-Abrechnung mit dem Eintrag. Reicht das?", attachments: ["Amex_April_2026.pdf"] },
  { id: "m3", author: "SB", name: "Sabine Steuerberater", time: "Heute 11:45", body: "Normalerweise verlangt das Finanzamt den Original-Beleg. Die Kreditkarten-Abrechnung hilft als Ergänzung, aber ohne Original wird die Betriebsausgabe eventuell abgelehnt. Frag doch im Restaurant nach einer Rechnungsduplikat." },
];

const fakeFolders = [
  { name: "Kontoauszüge", count: 12 },
  { name: "Verträge", count: 4 },
  { name: "Finanzamt", count: 7 },
  { name: "Steuerberechnungen", count: 3 },
  { name: "UStVA-Bescheide", count: 6 },
  { name: "Sonstiges", count: 2 },
];

const fakeFiles = [
  { name: "Kontoauszug_März_2026.pdf", date: "02.04.2026", uploader: "Lena", quarter: "Q1 2026", version: 1, readOnly: false },
  { name: "Steuerberechnung_Q1_2026.pdf", date: "19.04.2026", uploader: "Sabine", quarter: "Q1 2026", version: 2, readOnly: true },
  { name: "Vertrag_Nebula_GmbH.pdf", date: "15.03.2026", uploader: "Lena", quarter: "Q1 2026", version: 1, readOnly: false },
];

// ─────────────────────────── Primitives ────────────────────────────
const SectionHeader = ({ num, title, subtitle }) => (
  <div className="mb-5 flex items-start gap-4 border-l-2 border-emerald-500 pl-4">
    <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-emerald-600">{`Preview ${num}`}</div>
    <div>
      <h2 className="text-xl font-semibold tracking-tight text-slate-900" style={H}>{title}</h2>
      {subtitle && <p className="mt-0.5 text-sm text-slate-500">{subtitle}</p>}
    </div>
  </div>
);

const TopNav = ({ active = "Steuerberater", role = "FL", badge = 2 }) => {
  const items = role === "FL"
    ? [
        { label: "Dashboard", Icon: LayoutDashboard },
        { label: "Kunden", Icon: Users },
        { label: "Projekte", Icon: Briefcase },
        { label: "Zeiterfassung", Icon: Clock },
        { label: "Rechnungen", Icon: Receipt },
        { label: "Finanzen", Icon: Wallet, badge: 4 },
        { label: "Steuern", Icon: Calculator },
        { label: "Steuerberater", Icon: InboxIcon, badge },
        { label: "Einstellungen", Icon: null },
      ]
    : [
        { label: "Übersicht", Icon: LayoutDashboard },
        { label: "Mandanten", Icon: Users },
        { label: "Posteingang", Icon: InboxIcon, badge: 6 },
        { label: "Einstellungen", Icon: null },
      ];
  return (
    <header className="border-b border-slate-200 bg-white">
      <div className="flex h-16 items-center gap-1 px-4 sm:px-6 lg:px-8">
        <div className="mr-4 flex items-center gap-2">
          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-emerald-600">
            <Calculator className="h-5 w-5 text-white" />
          </div>
          <span className="font-semibold tracking-tight text-slate-900" style={H}>ivoliq</span>
          {role === "SB" && <span className="ml-1 rounded-full border border-slate-300 px-1.5 py-0.5 text-[10px] uppercase tracking-widest text-slate-500">SB</span>}
        </div>
        <nav className="flex flex-1 items-center gap-1 overflow-x-auto">
          {items.map((item) => {
            const isActive = item.label === active;
            return (
              <div
                key={item.label}
                className={`inline-flex cursor-pointer items-center gap-2 rounded-lg px-3 py-2 text-sm transition-colors ${
                  isActive ? "bg-emerald-100 font-medium text-emerald-700" : "text-slate-600 hover:bg-slate-50"
                }`}
              >
                {item.Icon && <item.Icon className="h-4 w-4" />}
                <span className="hidden md:inline">{item.label}</span>
                {item.badge && (
                  <span className="ml-0.5 inline-flex h-5 min-w-[20px] items-center justify-center rounded-full bg-emerald-600 px-1.5 text-[10px] font-bold text-white">
                    {item.badge}
                  </span>
                )}
              </div>
            );
          })}
        </nav>
        <button className="ml-2 text-slate-400 hover:text-slate-700" title="Ausloggen">
          <LogOut className="h-4 w-4" />
        </button>
      </div>
    </header>
  );
};

// Matches KPICard from Dashboard.js exactly
const KPICard = ({ title, value, subtitle, Icon, iconColor = "bg-emerald-50 text-emerald-600", borderColor = "border-t-emerald-500", trend }) => (
  <Card className={`relative overflow-hidden border-t-4 ${borderColor}`}>
    <CardContent className="p-5 sm:p-6">
      <div className="flex items-start justify-between">
        <div className="space-y-1">
          <p className="text-xs font-medium uppercase tracking-[0.05em] text-slate-500">{title}</p>
          <p className="font-mono text-3xl font-semibold tracking-tight text-slate-900 sm:text-4xl">{value}</p>
          {subtitle && <p className="text-xs text-slate-500">{subtitle}</p>}
        </div>
        <div className="flex flex-col items-end gap-2">
          <div className={`rounded-xl p-2.5 ${iconColor}`}>
            <Icon className="h-5 w-5" />
          </div>
          {trend}
        </div>
      </div>
    </CardContent>
  </Card>
);

const TypeIcon = ({ type, className = "h-4 w-4 text-slate-500" }) => {
  const map = { question: MessageCircle, task: CheckSquare, request: FileQuestion, note: StickyNote };
  const Icon = map[type] || MessageCircle;
  return <Icon className={className} />;
};

const Pill = ({ variant = "neutral", children }) => {
  const v = {
    urgent: "bg-amber-50 text-amber-700 border-amber-200",
    waiting: "bg-blue-50 text-blue-700 border-blue-200",
    done: "bg-emerald-50 text-emerald-700 border-emerald-200",
    locked: "bg-slate-100 text-slate-600 border-slate-200",
    neutral: "bg-slate-50 text-slate-600 border-slate-200",
  }[variant];
  return <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-medium ${v}`}>{children}</span>;
};

// ───────────────────────── 1. SB-Triage-Dashboard ─────────────────────────
const TriageDashboard = () => (
  <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
    <TopNav role="SB" active="Übersicht" badge={6} />

    <div className="bg-slate-50 px-6 py-8">
      <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight text-slate-900" style={H}>Guten Morgen, Sabine</h1>
          <p className="mt-1 text-sm text-slate-500">
            <span className="font-mono font-medium text-slate-900">6</span> Vorgänge warten auf dich — verteilt auf <span className="font-mono font-medium text-slate-900">3</span> Mandanten.
          </p>
        </div>
        <Button className="bg-emerald-600 text-white hover:bg-emerald-700">
          <UserPlus className="mr-2 h-4 w-4" />
          Mandant einladen
        </Button>
      </div>

      {/* KPI Row (uses exact KPICard pattern) */}
      <div className="mb-8 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <KPICard title="Warten auf mich" value="6" subtitle="über 3 Mandanten" Icon={InboxIcon} borderColor="border-t-emerald-500" iconColor="bg-emerald-50 text-emerald-600" />
        <KPICard title="Quartale zur Freigabe" value="2" subtitle="Q1 2026 fällig" Icon={CheckSquare} borderColor="border-t-blue-500" iconColor="bg-blue-50 text-blue-600" />
        <KPICard title="Frisch hochgeladen" value="8" subtitle="Belege letzte 24h" Icon={FileText} borderColor="border-t-slate-500" iconColor="bg-slate-100 text-slate-600" />
        <KPICard title="Überfällig" value="1" subtitle="> 7 Tage offen" Icon={AlertCircle} borderColor="border-t-amber-500" iconColor="bg-amber-50 text-amber-600" />
      </div>

      {/* Action Card — left amber border like Handlungsbedarf */}
      <Card className="mb-8 border-l-4 border-l-emerald-500">
        <CardContent className="p-5 sm:p-6">
          <div className="mb-4 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="rounded-lg bg-emerald-50 p-1.5">
                <TrendingUp className="h-4 w-4 text-emerald-600" />
              </div>
              <h2 className="text-base font-semibold text-slate-900" style={H}>Heute brauchen dich</h2>
            </div>
            <span className="text-xs text-slate-500">sortiert nach Dringlichkeit</span>
          </div>
          <div className="space-y-2">
            {fakeClients.map((c) => (
              <div key={c.id} className="flex items-center gap-3 rounded-lg p-3 transition-colors hover:bg-slate-50">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-emerald-50 font-mono text-xs font-semibold text-emerald-700">
                  {c.initials}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-slate-900">{c.name}</p>
                  <p className="text-xs text-slate-500">zuletzt aktiv {c.last}</p>
                </div>
                {c.urgent > 0 && <Pill variant="urgent">{c.urgent} dringend</Pill>}
                <Pill variant="neutral">{c.waiting_for_me} Vorgänge</Pill>
                <ChevronRight className="h-4 w-4 text-slate-400" />
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Secondary — Collapsible */}
      <Card>
        <CardContent className="p-5 sm:p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Users className="h-4 w-4 text-slate-400" />
              <h2 className="text-sm font-semibold text-slate-900">Alle Mandanten</h2>
              <span className="font-mono text-xs text-slate-500">12 gesamt</span>
            </div>
            <Button variant="ghost" size="sm" className="text-slate-500">
              Anzeigen <ChevronRight className="ml-1 h-3 w-3" />
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  </div>
);

// ───────────────────────── 2. FL-Posteingang ─────────────────────────
const FLInbox = () => (
  <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
    <TopNav role="FL" active="Steuerberater" badge={2} />

    <div className="bg-slate-50 px-6 py-8">
      <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight text-slate-900" style={H}>Steuerberater</h1>
          <p className="mt-1 text-sm text-slate-500">Alles zwischen dir und deinem Steuerberater.</p>
        </div>
        <Button variant="outline" className="border-slate-200">
          <Share2 className="mr-2 h-4 w-4" />
          Magic-Link teilen
        </Button>
      </div>

      <div className="mb-6 grid grid-cols-1 gap-4 lg:grid-cols-3">
        {/* Advisor Card */}
        <Card>
          <CardContent className="p-5">
            <p className="mb-3 text-xs font-medium uppercase tracking-[0.05em] text-slate-500">Mein Steuerberater</p>
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-emerald-50 font-mono text-xs font-semibold text-emerald-700">SB</div>
              <div>
                <p className="text-sm font-medium text-slate-900">Sabine Steuerberater</p>
                <p className="text-xs text-slate-500">Kanzlei Mayer &amp; Co., München</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Permissions Card */}
        <Card className="lg:col-span-2">
          <CardContent className="p-5">
            <p className="mb-3 text-xs font-medium uppercase tracking-[0.05em] text-slate-500">Was sieht dein Steuerberater?</p>
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
              {[
                { label: "Dashboard", on: true },
                { label: "Rechnungen", on: true },
                { label: "Ausgaben", on: true },
                { label: "Belege", on: true },
                { label: "Steuern", on: false },
                { label: "Zeiterfassung", on: false },
              ].map((p) => (
                <div key={p.label} className={`flex items-center justify-between rounded-lg border px-3 py-2 text-xs ${p.on ? "border-emerald-200 bg-emerald-50 text-emerald-900" : "border-slate-200 bg-slate-50 text-slate-500"}`}>
                  <span>{p.label}</span>
                  <span className={`h-1.5 w-1.5 rounded-full ${p.on ? "bg-emerald-500" : "bg-slate-300"}`} />
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Open Items */}
      <Card>
        <CardContent className="p-5">
          <div className="mb-4 flex items-center justify-between">
            <h2 className="text-base font-semibold text-slate-900" style={H}>Offene Vorgänge</h2>
            <span className="font-mono text-xs text-slate-500">2 offen</span>
          </div>
          <div className="space-y-2">
            {fakeThreads.slice(0, 2).map((t) => (
              <div key={t.id} className="flex items-start gap-3 rounded-lg p-3 transition-colors hover:bg-slate-50">
                <div className="flex-shrink-0 rounded-lg bg-slate-100 p-2">
                  <TypeIcon type={t.type} className="h-4 w-4 text-slate-600" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-medium text-slate-900">{t.subject}</p>
                    {t.priority === "urgent" && <Pill variant="urgent">Dringend</Pill>}
                  </div>
                  <p className="mt-0.5 text-xs text-slate-500">{t.preview}</p>
                </div>
                <span className="font-mono text-xs text-slate-400">{t.time}</span>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  </div>
);

// ───────────────────────── 3. Unified Inbox ─────────────────────────
const UnifiedInbox = () => {
  const [selected, setSelected] = useState("t1");
  const thread = fakeThreads.find((t) => t.id === selected);
  return (
    <Card className="overflow-hidden">
      <div className="flex min-h-[600px]">
        {/* Thread List */}
        <div className="w-full max-w-sm border-r border-slate-200 bg-slate-50">
          <div className="border-b border-slate-200 bg-white px-4 py-3">
            <div className="mb-3 flex flex-wrap gap-1">
              {["Wartet auf mich", "Wartet auf SB", "Erledigt", "Alle"].map((f, i) => (
                <button
                  key={f}
                  className={`rounded-full px-3 py-1 text-[11px] font-medium transition-colors ${
                    i === 0
                      ? "bg-emerald-600 text-white"
                      : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                  }`}
                >
                  {f}
                </button>
              ))}
            </div>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <input
                className="w-full rounded-lg border border-slate-200 bg-white py-2 pl-9 pr-3 text-sm placeholder:text-slate-400 focus:border-emerald-500 focus:outline-none focus:ring-0"
                placeholder="Suchen…"
              />
            </div>
          </div>
          <div className="divide-y divide-slate-100">
            {fakeThreads.map((t) => (
              <button
                key={t.id}
                onClick={() => setSelected(t.id)}
                className={`flex w-full items-start gap-3 px-4 py-3 text-left transition-colors ${
                  selected === t.id ? "bg-white" : "hover:bg-white/60"
                }`}
              >
                <div className={`flex-shrink-0 rounded-lg p-2 ${selected === t.id ? "bg-emerald-50" : "bg-slate-100"}`}>
                  <TypeIcon type={t.type} className={`h-4 w-4 ${selected === t.id ? "text-emerald-600" : "text-slate-500"}`} />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <p className={`truncate text-sm ${t.unread ? "font-semibold text-slate-900" : "font-medium text-slate-700"}`}>
                      {t.subject}
                    </p>
                    {t.priority === "urgent" && (
                      <span className="rounded-full border border-amber-200 bg-amber-50 px-1.5 text-[10px] font-bold text-amber-700">!</span>
                    )}
                  </div>
                  <p className="mt-0.5 truncate text-xs text-slate-500">{t.preview}</p>
                  <div className="mt-1 flex items-center justify-between">
                    <span className="text-[10px] uppercase tracking-wider text-slate-400">
                      {t.owner === "Du" ? "Du bist dran" : `${t.owner} ist dran`}
                    </span>
                    <span className="font-mono text-[10px] text-slate-400">{t.time}</span>
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Thread Detail */}
        <div className="flex flex-1 flex-col bg-white">
          <div className="flex items-start justify-between border-b border-slate-200 px-6 py-4">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <div className="rounded-lg bg-emerald-50 p-1.5">
                  <TypeIcon type={thread?.type} className="h-4 w-4 text-emerald-600" />
                </div>
                <h3 className="text-base font-semibold text-slate-900" style={H}>{thread?.subject}</h3>
              </div>
              <p className="mt-1 text-xs text-slate-500">Lena Scholz · verknüpft mit Beleg #2026-042 · {thread?.time}</p>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm">
                <CheckSquare className="mr-1 h-3 w-3" /> Erledigt
              </Button>
              <Button variant="ghost" size="sm">Archivieren</Button>
            </div>
          </div>

          <div className="flex-1 space-y-5 overflow-y-auto bg-slate-50 p-6">
            {fakeMessages.map((m) => (
              <div key={m.id} className="flex gap-3">
                <div className={`flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full font-mono text-xs font-semibold ${
                  m.author === "SB" ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"
                }`}>
                  {m.author}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="mb-1 flex items-baseline gap-2">
                    <p className="text-sm font-medium text-slate-900">{m.name}</p>
                    <p className="font-mono text-[11px] text-slate-400">{m.time}</p>
                  </div>
                  <div className="rounded-xl border border-slate-200 bg-white p-4 text-sm leading-relaxed text-slate-700">
                    {m.body}
                    {m.attachments && (
                      <div className="mt-3 inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-1.5 text-xs text-slate-600">
                        <Paperclip className="h-3 w-3" />
                        {m.attachments[0]}
                        <Download className="ml-2 h-3 w-3 cursor-pointer text-slate-400 hover:text-slate-700" />
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Reply Box */}
          <div className="border-t border-slate-200 bg-white p-4">
            <div className="rounded-xl border border-slate-200 bg-white">
              <textarea
                rows="2"
                placeholder="Antwort schreiben…"
                className="w-full resize-none rounded-t-xl border-0 p-3 text-sm placeholder:text-slate-400 focus:outline-none focus:ring-0"
              />
              <div className="flex items-center justify-between border-t border-slate-100 px-3 py-2">
                <button className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700" title="Datei anhängen">
                  <Paperclip className="h-4 w-4" />
                </button>
                <Button size="sm" className="bg-emerald-600 text-white hover:bg-emerald-700">
                  <Send className="mr-1 h-3 w-3" />
                  Senden
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Card>
  );
};

// ───────────────────────── 4. Tresor ─────────────────────────
const Tresor = () => (
  <Card className="overflow-hidden">
    <div className="flex min-h-[500px] bg-slate-50">
      <aside className="w-60 border-r border-slate-200 bg-white py-4">
        <p className="mb-2 px-4 text-xs font-semibold uppercase tracking-[0.08em] text-slate-500">Ordner</p>
        {fakeFolders.map((f, i) => (
          <div key={f.name} className={`mx-2 mb-1 flex items-center justify-between rounded-lg px-3 py-2 text-sm transition-colors ${
            i === 0
              ? "bg-emerald-50 font-medium text-emerald-900"
              : "text-slate-600 hover:bg-slate-50"
          }`}>
            <span className="flex items-center gap-2">
              <Folder className={`h-4 w-4 ${i === 0 ? "text-emerald-600" : "text-slate-400"}`} />
              {f.name}
            </span>
            <span className="font-mono text-xs text-slate-400">{f.count}</span>
          </div>
        ))}
        <div className="mt-3 px-4">
          <button className="w-full rounded-lg border border-dashed border-slate-300 bg-white px-3 py-1.5 text-xs text-slate-500 hover:bg-slate-50">
            + Ordner anlegen
          </button>
        </div>
      </aside>

      <main className="flex-1 p-6">
        <div className="mb-5 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <div className="flex items-center gap-2">
              <FolderOpen className="h-5 w-5 text-emerald-600" />
              <h2 className="text-xl font-semibold tracking-tight text-slate-900" style={H}>Kontoauszüge</h2>
            </div>
            <p className="mt-0.5 font-mono text-xs text-slate-500">12 Dateien · letzter Upload vor 2 Tagen</p>
          </div>
          <div className="flex items-center gap-2">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <input className="rounded-lg border border-slate-200 bg-white py-2 pl-9 pr-3 text-sm placeholder:text-slate-400 focus:border-emerald-500 focus:outline-none" placeholder="Dateien suchen…" />
            </div>
            <Button className="bg-emerald-600 text-white hover:bg-emerald-700">
              <Upload className="mr-2 h-4 w-4" />
              Hochladen
            </Button>
          </div>
        </div>

        <Card className="overflow-hidden">
          <table className="w-full text-left text-sm">
            <thead className="border-b border-slate-200 bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
              <tr>
                <th className="px-5 py-3 font-medium">Name</th>
                <th className="px-5 py-3 font-medium">Datum</th>
                <th className="px-5 py-3 font-medium">Von</th>
                <th className="px-5 py-3 font-medium">Quartal</th>
                <th className="px-5 py-3 text-right font-medium">Aktion</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {fakeFiles.map((f) => (
                <tr key={f.name} className="transition-colors hover:bg-slate-50">
                  <td className="px-5 py-3">
                    <div className="flex items-center gap-2">
                      <FileText className="h-4 w-4 text-slate-400" />
                      <span className="font-medium text-slate-900">{f.name}</span>
                      {f.version > 1 && <span className="rounded-full bg-slate-100 px-2 py-0.5 font-mono text-[10px] font-medium text-slate-600">v{f.version}</span>}
                      {f.readOnly && <Lock className="h-3 w-3 text-slate-400" />}
                    </div>
                  </td>
                  <td className="px-5 py-3 font-mono text-slate-500">{f.date}</td>
                  <td className="px-5 py-3">
                    <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-slate-100 font-mono text-[10px] font-semibold text-slate-700">
                      {f.uploader[0]}
                    </span>
                    <span className="ml-2 text-slate-700">{f.uploader}</span>
                  </td>
                  <td className="px-5 py-3"><Pill variant="neutral">{f.quarter}</Pill></td>
                  <td className="px-5 py-3 text-right">
                    <button className="rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700" title="Ansehen">
                      <Eye className="h-4 w-4" />
                    </button>
                    <button className="rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700" title="Download">
                      <Download className="h-4 w-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      </main>
    </div>
  </Card>
);

// ───────────────────────── 5. ClientDetail ─────────────────────────
const ClientDetail = () => {
  const [modalOpen, setModalOpen] = useState(false);
  const quarters = [
    { q: "Q1 2026", state: "Festgeschrieben", color: "locked", Icon: Lock },
    { q: "Q2 2026", state: "Freigegeben", color: "done", Icon: CheckSquare },
    { q: "Q3 2026", state: "In Prüfung", color: "waiting", Icon: Clock },
    { q: "Q4 2026", state: "Offen", color: "neutral", Icon: null },
  ];
  return (
    <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
      <TopNav role="SB" active="Mandanten" badge={6} />

      <div className="bg-slate-50 px-6 py-6">
        <div className="mb-5 flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <p className="mb-1 text-xs uppercase tracking-[0.08em] text-slate-500">Mandant</p>
            <h1 className="text-2xl font-semibold tracking-tight text-slate-900" style={H}>Lena Scholz</h1>
            <p className="mt-1 text-sm text-slate-500">Designerin · Plan Pro · Einzelunternehmen (DE) · 2026</p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm">
              <InboxIcon className="mr-1.5 h-3.5 w-3.5" />
              Posteingang
              <span className="ml-2 inline-flex h-5 min-w-[20px] items-center justify-center rounded-full bg-emerald-600 px-1.5 text-[10px] font-bold text-white">2</span>
            </Button>
            <Button className="bg-emerald-600 text-white hover:bg-emerald-700">Export</Button>
          </div>
        </div>

        <Card className="mb-4">
          <CardContent className="p-4">
            <p className="mb-2 text-xs font-semibold uppercase tracking-[0.08em] text-slate-500">Periodenstatus 2026</p>
            <div className="flex flex-wrap items-center gap-2">
              {quarters.map((qu) => (
                <div key={qu.q} className="flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-1.5">
                  <span className="font-mono text-xs font-semibold text-slate-700">{qu.q}</span>
                  <Pill variant={qu.color}>
                    {qu.Icon && <qu.Icon className="mr-1 h-2.5 w-2.5" />}
                    {qu.state}
                  </Pill>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Tabs — emerald active, pill-style */}
        <div className="mb-4 inline-flex items-center gap-1 rounded-lg border border-slate-200 bg-white p-1">
          {[
            { label: "Übersicht", active: false },
            { label: "Belege & Rechnungen", active: true },
            { label: "Tresor", active: false },
            { label: "Posteingang", active: false, badge: 2 },
          ].map((t) => (
            <div key={t.label} className={`inline-flex cursor-pointer items-center gap-2 rounded-md px-4 py-1.5 text-sm transition-colors ${
              t.active ? "bg-emerald-50 font-medium text-emerald-700" : "text-slate-600 hover:bg-slate-50"
            }`}>
              {t.label}
              {t.badge && <span className="inline-flex h-4 min-w-[16px] items-center justify-center rounded-full bg-emerald-600 px-1 text-[10px] font-bold text-white">{t.badge}</span>}
            </div>
          ))}
        </div>

        <Card>
          <CardContent className="p-5">
            <div className="mb-3 flex items-center justify-between">
              <h3 className="text-base font-semibold text-slate-900" style={H}>Rechnungen</h3>
              <label className="flex items-center gap-2 text-xs text-slate-500">
                <input type="checkbox" className="h-3 w-3 rounded border-slate-300" />
                Festgeschriebenes anzeigen
              </label>
            </div>
            <div className="overflow-hidden rounded-lg border border-slate-200">
              <table className="w-full text-left text-sm">
                <thead className="border-b border-slate-200 bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
                  <tr>
                    <th className="px-4 py-3 font-medium">Nummer</th>
                    <th className="px-4 py-3 font-medium">Datum</th>
                    <th className="px-4 py-3 font-medium">Kunde</th>
                    <th className="px-4 py-3 font-medium">Betrag</th>
                    <th className="px-4 py-3 text-right font-medium">Aktion</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  <tr className="bg-slate-50/60 text-slate-500">
                    <td className="px-4 py-3">
                      <span className="flex items-center gap-2 font-mono">
                        <Lock className="h-3.5 w-3.5" />
                        2026-001
                      </span>
                    </td>
                    <td className="px-4 py-3 font-mono">12.01.2026</td>
                    <td className="px-4 py-3">Nebula GmbH</td>
                    <td className="px-4 py-3 font-mono">€ 2.100,00</td>
                    <td className="px-4 py-3 text-right">
                      <button onClick={() => setModalOpen(true)} className="text-xs text-slate-500 underline hover:text-amber-700">Bearbeiten</button>
                    </td>
                  </tr>
                  <tr className="hover:bg-slate-50">
                    <td className="px-4 py-3 font-mono font-medium text-slate-900">2026-017</td>
                    <td className="px-4 py-3 font-mono text-slate-700">18.04.2026</td>
                    <td className="px-4 py-3 text-slate-700">Studio Klee (AT)</td>
                    <td className="px-4 py-3 font-mono text-slate-900">€ 3.850,00</td>
                    <td className="px-4 py-3 text-right">
                      <button className="text-xs font-medium text-emerald-700 hover:underline">Bearbeiten</button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      </div>

      {modalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 px-4">
          <Card className="w-full max-w-lg">
            <CardContent className="p-6">
              <div className="flex items-start gap-3">
                <div className="rounded-full bg-amber-50 p-2">
                  <AlertCircle className="h-6 w-6 text-amber-600" />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold tracking-tight text-slate-900" style={H}>Festgeschriebenes Quartal aufbrechen?</h3>
                  <p className="mt-2 text-sm text-slate-600">
                    Diese Rechnung gehört zu <strong className="text-slate-900">Q1 2026</strong>, das bereits festgeschrieben ist. Eine Änderung wird protokolliert und kann nicht rückgängig gemacht werden.
                  </p>
                  <label className="mt-4 block text-[11px] font-semibold uppercase tracking-[0.08em] text-slate-500">Begründung (Pflicht)</label>
                  <textarea rows="3" placeholder="z.B. Tippfehler bei Rechnungsbetrag" className="mt-1.5 w-full rounded-lg border border-slate-200 bg-white p-3 text-sm placeholder:text-slate-400 focus:border-emerald-500 focus:outline-none focus:ring-0" />
                </div>
              </div>
              <div className="mt-5 flex justify-end gap-2">
                <Button variant="outline" onClick={() => setModalOpen(false)}>Abbrechen</Button>
                <Button className="bg-amber-600 text-white hover:bg-amber-700">Aufbrechen &amp; bearbeiten</Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

// ───────────────────────── Main ─────────────────────────
export default function DesignPreview() {
  return (
    <div className="min-h-screen bg-slate-100 px-4 py-10 sm:px-8 lg:px-16" style={{ fontFamily: "DM Sans, sans-serif" }}>
      <link rel="preconnect" href="https://fonts.googleapis.com" />
      <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="" />
      <link rel="stylesheet" href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&family=JetBrains+Mono:wght@400;600&family=Manrope:wght@400;500;600;700&display=swap" />

      <header className="mx-auto mb-8 max-w-6xl border-l-2 border-emerald-500 pl-4">
        <p className="text-[10px] font-semibold uppercase tracking-[0.25em] text-emerald-600">Design Preview · v3 (FL-App-Style)</p>
        <h1 className="mt-1 text-2xl font-semibold tracking-tight text-slate-900" style={H}>
          Collaboration Redesign — integriert mit FL-App-Design
        </h1>
        <p className="mt-2 max-w-3xl text-sm text-slate-600">
          Nutzt jetzt die Design-Sprache von Dashboard/Finanzen/Steuern: Top-Navigation mit grünen Pill-Items, <code className="rounded bg-slate-100 px-1 text-xs">shadcn/Card</code> mit <code className="rounded bg-slate-100 px-1 text-xs">border-t-4</code>, Icon-Boxen in runden Hintergründen, emerald-600 als Primary, rounded-xl/lg, <code className="rounded bg-slate-100 px-1 text-xs">font-mono</code> für Zahlen.
        </p>
      </header>

      <div className="mx-auto max-w-6xl space-y-12">
        <section>
          <SectionHeader num="1" title="SB-Triage-Dashboard" subtitle="Ersetzt die Mandantenliste als Landing — zeigt sofort, wer dich heute braucht." />
          <TriageDashboard />
        </section>
        <section>
          <SectionHeader num="2" title="FL-Posteingang (Top-Level-Menü)" subtitle={'„Steuerberater" als eigener Menüpunkt im FL-Hauptmenü mit Badge-Count.'} />
          <FLInbox />
        </section>
        <section>
          <SectionHeader num="3" title="Unified Inbox (Two-Pane)" subtitle="Ersetzt Kommentare + Aufgaben + Dokumentanfragen als einheitliche Thread-Ansicht." />
          <UnifiedInbox />
        </section>
        <section>
          <SectionHeader num="4" title="Tresor — Dokument-Austausch" subtitle="Beidseitig: FL lädt Kontoauszüge, SB lädt Steuerberechnungen." />
          <Tresor />
        </section>
        <section>
          <SectionHeader num="5" title="ClientDetail 4-Tab + Period-Lock" subtitle={'Von 7 auf 4 Tabs reduziert. Klick auf „Bearbeiten" einer festgeschriebenen Rechnung → Warnmodal.'} />
          <ClientDetail />
        </section>
      </div>

      <footer className="mx-auto mt-16 max-w-6xl text-center text-xs text-slate-400">
        Mockup · keine echten Daten · ivoliq Design Preview v3 · April 2026
      </footer>
    </div>
  );
}
